import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { IRequestOptions, request } from 'services/base'

interface UseDataActionsProps<TModel> {
  handleChange?: (key: keyof TModel, data: TModel) => void
  patchApi: () => string
  postApi: () => string
  formatData?: (values: TModel) => TModel
  headerRequest?: IRequestOptions['headers']
  defaultValue?: TModel
  unMaskedKeys?: Array<keyof TModel>
  fieldsNotNull?: Array<keyof TModel>
}

export const useDataActions = <TModel extends Record<string, any>>(
  props: UseDataActionsProps<TModel>
) => {
  const [data, setData] = useState<TModel>((props.defaultValue ?? {}) as TModel)
  const [dataCopy, setDataCopy] = useState<TModel>(
    (props.defaultValue ?? {}) as TModel
  )

  type ModelKeys = keyof TModel
  type ErrorMap = Map<keyof TModel, string>

  const [state, setState] = useState<IStateStdTypes>('idle')

  const [editMode, setEditMode] = useState<boolean>(false)

  const [errorsMap, setErrorsMap] = useState<ErrorMap>(new Map())

  const location = useLocation()

  const getData = () => {
    return props.formatData ? props.formatData(dataCopy) : data
  }

  const addNewData = async (): Promise<any> => {
    setState('loading')

    const formated = getData()

    const response = await request({
      url: props.postApi(),
      method: 'post',
      data: formated,
      headers: props.headerRequest
    })

    if (response.success) {
      setDataCopy(data)

      setState('success')
      return response.data
    }

    setState('error')
    if (
      response.errors?.some(
        error =>
          error?.detail?.includes('duplicate key value') ||
          error?.detail?.includes('duplicar valor da chave')
      )
    ) {
      throw new Error('duplicate')
    } else {
      throw new Error('error')
    }
  }

  const editData = async (): Promise<any> => {
    setState('loading')
    const formatedData = getData()

    const response = await request<TModel>({
      url: props.patchApi(),
      method: 'patch',
      data: formatedData,
      headers: props.headerRequest
    })

    if (response.success && response.data) {
      setState('success')

      setDataCopy(data)
      return response.data
    }

    setState('error')
    throw new Error('error')
  }

  const choseOperation = () => {
    if (editMode) return editData

    return addNewData
  }

  const sendRequest = async () => {
    if (state == 'loading') return

    const operation = choseOperation()

    return operation()
  }

  const resetInputError = (key: ModelKeys) => {
    if (errorsMap.has(key)) {
      errorsMap.delete(key)
      setErrorsMap(new Map(errorsMap))
    }
  }

  const change = (key: ModelKeys, value: any) => {
    const newData: TModel = {
      ...data,
      [key]: value
    }

    if (props.handleChange) props.handleChange(key, newData)

    resetInputError(key)
    setData(newData)
  }

  // Helper function to compare arrays
  const arraysAreEqual = (arr1: any[], arr2: any[]): boolean => {
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
      return false
    }

    if (arr1.length !== arr2.length) {
      return false
    }

    const arr1Set = new Set(arr1)

    return arr2.every(item => arr1Set.has(item))
  }

  // Helper function to compare values (handles arrays and primitive values)
  const valuesAreEqual = (origin: any, value: any): boolean => {
    if (Array.isArray(origin) && Array.isArray(value)) {
      return arraysAreEqual(origin, value)
    }

    return origin === value
  }

  const enableButton = () => {
    if (editMode) {
      for (const key in data) {
        let origin: any
        let value: any
        if (props.unMaskedKeys?.includes(key)) {
          origin = dataCopy[key]?.replaceAll(/[^\d]/g, '')
          value = data[key]?.replaceAll(/[^\d]/g, '')
        } else {
          origin = dataCopy[key]
          value = data[key]
        }

        if (!valuesAreEqual(origin, value)) {
          return true
        }
      }
      return false
    }

    return props.fieldsNotNull
      ? !props.fieldsNotNull.some(key => data[key] == '')
      : true
  }

  useEffect(() => {
    const data: TModel | null | undefined = location.state

    if (data) {
      setData(data)
      setDataCopy(data)
    }

    setEditMode(!!data)
  }, [])

  return {
    data,
    editMode,
    sendRequest,
    change,
    state,
    setState,
    errorsMap,
    setErrorsMap,
    resetInputError,
    setData,
    enableButton: enableButton()
  }
}
